import { App } from '@capacitor/app';

import { apiUrl } from '../../../../front/src/config';
import axiosInstance from '../../../../front/src/services/axios';
import type { AccessToken, AuthResponse, RefreshToken } from '../../../../front/src/types/auth';
import { NATIVE_URL_SCHEME } from '../../../../front/src/utils/native';
import { devLogger } from '../../utils/logs';
import { finalizeNativeLogin } from './nativeLogin';

type TokensToLogWith = {
  type: 'basic' | 'long';
  tokens: AuthResponse;
};

export const initDeepLinkHandler = (websiteKey: string, locale: string) => {
  console.log('🔧 Initializing deep link handler for:', websiteKey, locale);

  App.addListener('appUrlOpen', async event => {
    console.log('🔗 Deep link received:', event.url);

    // For custom URL schemes, we need to parse manually
    const url = event.url;

    // Extract the path and params
    const urlParts = url.replace(`${NATIVE_URL_SCHEME}://`, '').split('?');

    const pathname = urlParts[0];
    devLogger('🔗 URL pathname:', pathname);
    console.log('🔗 URL pathname:', pathname);
    if (pathname !== 'auth-callback') return console.log('❌ Not an auth callback:', pathname);

    const searchParams = new URLSearchParams(urlParts[1] || '');
    console.log('🔗 URL params:', Object.fromEntries(searchParams.entries()));

    const token: AccessToken = searchParams.get('token');
    console.log('🔑 Token:', token ? 'present' : 'missing');
    if (!token) return console.log('❌ Token missing:');

    const refreshToken: RefreshToken = searchParams.get('refreshToken');
    console.log('🔑 Refresh token:', refreshToken ? 'present' : 'missing');

    const key = searchParams.get('key');
    console.log('🔑 Key:', key);
    if (!key) return console.log('❌ Key missing:');

    console.log('🔗 Expected key:', websiteKey);
    if (key !== websiteKey) return console.log('❌ Key mismatch:', { key, websiteKey });

    try {
      let tokensToLogWith: TokensToLogWith = {
        type: 'basic',
        tokens: { token, refreshToken: refreshToken || '' },
      };

      if (refreshToken) {
        // Refresh tokens to get long ones. L'api reconnaît l'app native au
        // drapeau `X-Native-App`, posé une fois pour toutes sur l'instance axios
        // par `useCapacitor` (derrière `Capacitor.isNativePlatform()`) : il part
        // donc avec cette requête sans qu'on ait à le répéter ici.
        //
        // ⛔ Avant le 2026-09-21, cette requête portait un `X-Native-Id` : un JWT
        // signé ICI, dans le navigateur — donc avec un secret publié dans le
        // bundle client (fuite du 21/09). Un jeton que n'importe qui peut forger
        // n'authentifiait rien de plus que le drapeau.
        // Détail et interdits : `services/jwt/JWTService.ts`.
        try {
          const { data: longTokens } = await axiosInstance.post<AuthResponse>(
            `${apiUrl}/api/pub/refresh-token`,
            { refreshToken },
            { headers: { website: websiteKey } },
          );

          if (longTokens) {
            // ⛔ Ne JAMAIS imprimer les jetons eux-mêmes : `webContentsDebuggingEnabled`
            // est à `true` (capacitor.config.js) et `next.config.js` n'a pas de
            // `compiler.removeConsole` — la console survit au build de prod et se lit
            // au Safari Web Inspector. Même règle que les lignes 36 et 40.
            console.log('🦄 ~ initDeepLinkHandler ~ longTokens: OK');
            tokensToLogWith = { type: 'long', tokens: longTokens };
          } else {
            console.warn('⚠️ Got empty long tokens from refresh route -> use basic tokens');
          }
        } catch (refreshError) {
          console.warn('⚠️ Failed to refresh tokens -> use basic tokens:', refreshError);
        }
      } else {
        console.warn("⚠️ Refresh token missing -> can't get long tokens");
      }

      console.log('✅ Processing auth callback...');
      console.log('🔑 Tokens to log with:', tokensToLogWith.type); // jamais les jetons, cf. ci-dessus

      await finalizeNativeLogin(websiteKey, locale, tokensToLogWith.tokens);
    } catch (error) {
      console.error('❌ Error handling deep link auth:', error);
      window.location.reload();
    }
  });
};
